<template>
  <div v-resize="resize">
    <v-data-table
      ref="table"
      :class="{
        'fin-data-table': true,
        'disable-row-hover': disableRowHover,
        'sticky-first-col': stickyFirstCol,
        'sticky-last-col': showSelect || stickyLastCol,
        transparent,
      }"
      :fixed-header="fixedHeader"
      :footer-props="{
        'items-per-page-options': [10, 15, 20, 50],
      }"
      :headers="_headersWithSelect"
      :height="_height"
      :hide-default-header="hideDefaultHeader"
      :hide-default-footer="hideDefaultFooter"
      :item-key="itemKey"
      :items="items"
      :item-class="itemClass"
      :loading="loading"
      :mobile-breakpoint="mobileBreakpoint"
      :no-data-text="noDataTextMsg"
      :options.sync="options"
      :server-items-length="total"
      :show-select="showSelect"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :value="value"
      @input="(val) => $emit('input', val)"
      @click:row="(data) => $emit('click:row', data)"
    >
      <template
        #body="{ items, isMobile, isSelected, select }"
        v-if="_mobileView && $vuetify.breakpoint.width < mobileBreakpoint"
      >
        <slot name="body.mobile">
          <tbody class="mobile-data-table">
          <tr v-if="!items || items.length === 0">
            <td>{{ noDataTextMsg }}</td>
          </tr>

          <tr
            :class="isSelected(item) ? 'active-item' : 'inactive-item'"
            :key="index"
            @click="() => select(item, !isSelected(item))"
            v-for="(item, index) in items"
          >
            <td :class="'px-1 ' + ($vuetify.breakpoint.xs ? 'py-3' : 'py-4')" style="flex: 1">
              <v-icon
                :color="$finColors.primaryDark"
                :size="30"
                style="float: right;padding-right: 10px;"
                v-if="showSelect"
              >
                {{
                  isSelected(item)
                    ? "icon-check-ellipse"
                    : "mdi-checkbox-blank-outline"
                }}
              </v-icon>

              <v-row no-gutters v-if="_mobileView.title">
                <template v-for="(header, rowindex) in _mobileView.title">
                  <v-spacer
                    :key="'spacer' + rowindex"
                    v-if="header.value === 'spacer'"
                  />
                  <v-col
                    v-else
                    :key="'title_' + rowindex"
                    :class="{ ['text-' + header.align]: header.align, ['col-auto']: header.autoCol, [header.autoColClasses]: header.autoColClasses }"
                    :style="'vertical-align: top' + (header.bold ?';font-weight: bold' : '') + (header.paddingTop ? ';padding-top:' + header.paddingTop : '') + (header.paddingRight ? ';padding-right:' + header.paddingRight : '') + (header.minWidth ? ';min-width:' + header.minWidth : '') + (header.maxWidth ? ';max-width:' + header.maxWidth : '')"
                  >
                    <template
                      v-if="
                          header.noValue ||
                          isValue(createItemValue(item, header))
                        "
                    >
                      <slot
                        :name="`item.${header.value}`"
                        v-bind="createItemProps(item, header, isMobile)"
                      >
                        <component
                          :is="header.component"
                          :key="index"
                          :class="['value', header.cellClass]"
                          v-bind="createItemProps(item, header, isMobile)"
                          v-if="header.component"
                        />
                        <div :key="'val_' + index" :class="['value',header.cellClass]" v-else>
                          {{ createItemValue(item, header) }}
                        </div>
                        <v-tooltip v-if="header.mobileHeaderAsTooltip" bottom>
                          <template #activator="{ on }">
                            <v-icon class="ml-1" v-on="on">icon-information</v-icon>
                          </template>
                          <span>{{ header.text }}</span>
                        </v-tooltip>
                      </slot>
                    </template>
                    <template v-else :class="['value',header.cellClass]">{{ emptyCell }}</template>
                  </v-col>
                </template>
              </v-row>

              <v-row v-if="_grid" dense>
                <v-col
                  :key="'grid' + rowindex"
                  :cols="getGridCols(header)"
                  :class="header.class"
                  :style="(header.minWidth ? 'min-width:' + header.minWidth : '')"
                  v-for="(header, rowindex) in _grid.headers"
                >
                  <slot
                    :name="`header.${header.value}`"
                    v-bind="createItemProps(item, header, isMobile)"
                  >
                    <div :key="'header' + index"
                         :class="['fin-data-table-header', 'mobile-header', {'d-inline-block': _gridInline, 'mr-2': _gridInline}]">
                      <fin-lexicon-text v-if="!header.textHideLexicon"
                                        :text="header.text"
                                        auto-solve
                                        class="d-inline"/>
                      <span v-else class="d-inline">{{ header.text }}</span>
                      <sup v-if="header.textSup" class="ml-1">{{ header.textSup }}</sup>
                      <span class="hint-index" v-if="header.hint">{{ `*` + (getHintIndex(header.hint) || '') }}</span>
                    </div>
                  </slot>

                  <div :class="{'d-inline-block': _gridInline}">
                    <template
                      v-if="
                        header.noValue || isValue(createItemValue(item, header))
                      "
                    >
                      <slot
                        :name="`item.${header.value}`"
                        v-bind="createItemProps(item, header)"
                      >
                        <component
                          :is="header.component"
                          :key="index"
                          :class="['value',header.cellClass]"
                          v-bind="createItemProps(item, header, isMobile)"
                          v-if="header.component"
                        />
                        <div :key="'val_' + index" :class="['value',header.cellClass]" v-else>
                          {{ createItemValue(item, header) }}
                        </div>

                        <!--TODO for all display variant and allow function-->
                        <div v-if="header.valueHint" class="value-hint">{{ header.valueHint }}</div>
                      </slot>
                    </template>
                    <template v-else :class="['value',header.cellClass]">{{ emptyCell }}</template>
                  </div>
                </v-col>
              </v-row>

              <template v-if="_list">
                <v-row
                  :key="'list' + rowindex"
                  v-bind="_listRowProps"
                  :class="header.class"
                  v-for="(header, rowindex) in _list.headers"
                >
                  <v-col class="py-2">
                    <slot
                      :name="`header.${header.value}`"
                      v-bind="createItemProps(item, header, isMobile)"
                    >
                      <div :key="'header' + index" class="fin-data-table-header mobile-header">
                        <fin-lexicon-text v-if="!header.textHideLexicon"
                                          :text="header.text"
                                          auto-solve
                                          class="d-inline"/>
                        <span v-else class="d-inline">{{ header.text }}</span>
                        <sup v-if="header.textSup" class="ml-1">{{ header.textSup }}</sup>
                        <span class="hint-index" v-if="header.hint">{{ `*` + (getHintIndex(header.hint) || '') }}</span>
                      </div>
                    </slot>
                  </v-col>
                  <v-col class="py-2" cols="auto">
                    <template
                      v-if="
                          header.noValue ||
                          isValue(createItemValue(item, header))
                        "
                    >
                      <slot
                        :name="`item.${header.value}`"
                        v-bind="createItemProps(item, header, isMobile)"
                      >
                        <component
                          :is="header.component"
                          :key="index"
                          :class="['value',header.cellClass]"
                          v-bind="createItemProps(item, header, isMobile)"
                          v-if="header.component"
                        />
                        <div :key="'val_' + index" :class="['value',header.cellClass]" v-else>
                          {{ createItemValue(item, header) }}
                        </div>
                      </slot>
                    </template>
                    <template v-else :class="['value',header.cellClass]">{{ emptyCell }}</template>
                  </v-col>
                </v-row>
              </template>

              <v-expansion-panels
                class="expansion"
                flat
                multiple
                v-if="_mobileView.expansion"
              >
                <v-expansion-panel
                  :key="'exp_' + expansionindex"
                  @click.stop.prevent="
                      () => {
                        /*prevent select*/
                      }
                    "
                  v-for="([key, value], expansionindex) in Object.entries(
                      _mobileView.expansion
                    )"
                >
                  <v-expansion-panel-header class="mobile-header">{{
                      key
                    }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row
                      :key="'list' + rowindex"
                      :class="header.class"
                      v-for="(header, rowindex) in value"
                    >
                      <v-col>
                        <slot
                          :name="`header.${header.value}`"
                          v-bind="createItemProps(item, header, isMobile)"
                        >
                          <div :key="'header' + index" class="fin-data-table-header mobile-header">
                            <fin-lexicon-text v-if="!header.textHideLexicon"
                                              :text="header.text"
                                              auto-solve
                                              class="d-inline"/>
                            <span v-else class="d-inline">{{ header.text }}</span>
                            <span class="hint-index" v-if="header.hint">{{
                                `*` + (getHintIndex(header.hint) || '')
                              }}</span>
                          </div>
                        </slot>
                      </v-col>

                      <v-col class="text-right" cols="auto">
                        <template
                          v-if="
                              header.noValue ||
                              isValue(createItemValue(item, header))
                            "
                        >
                          <slot
                            :name="`item.${header.value}`"
                            v-bind="createItemProps(item, header, isMobile)"
                          >
                            <component
                              :is="header.component"
                              :key="index"
                              :class="['value',header.cellClass]"
                              v-bind="createItemProps(item, header, isMobile)"
                              v-if="header.component"
                            />
                            <div :key="'val_' + index" :class="['value',header.cellClass]" v-else>
                              {{ createItemValue(item, header) }}
                            </div>
                          </slot>
                        </template>
                        <template v-else :class="['value',header.cellClass]">{{ emptyCell }}</template>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </td>
          </tr>
          </tbody>
        </slot>
      </template>

      <template
        #[`header.${header.value}`]="props"
        v-for="(header, index) in _headers"
      >
        <slot :name="`header.${header.value}`" v-bind="props">
          <div :key="'header' + index" v-style:textAlign="header.textAlign ? header.textAlign : 'center'"
               class="fin-data-table-header header">
            <fin-lexicon-text :text="header.text"
                              auto-solve
                              v-if="!header.textHideLexicon"
                              class="d-inline"/>
            <span v-else class="d-inline">{{ header.text }}</span>
            <sup v-if="header.textSup" class="ml-1">{{ header.textSup }}</sup>
            <span class="hint-index" v-if="header.hint">{{ `*` + (getHintIndex(header.hint) || '') }}</span>
          </div>
        </slot>
      </template>

      <template
        #[`item.${header.value}`]="props"
        v-for="(header, index) in _headers"
      >
        <slot :name="`item.${header.value}`" v-bind="props">
          <template v-if="header.noValue || isValue(props.value)">
            <component
              :is="header.component"
              :key="index"
              v-bind="props"
              v-if="header.component"
              :class="['value',header.cellClass]"
            />
            <div :key="index" :class="['value',header.cellClass]" v-else>
              {{ props.value }}
            </div>
          </template>
          <template v-else :class="['value',header.cellClass]">{{ emptyCell }}</template>
        </slot>
      </template>
    </v-data-table>
    <div class="hints">
      <span class="hint px-8" :key="hint.key" v-for="(hint, index) in Object.values(_hints)">
        <span class="hint-index">{{ `*${index || ''}` }}</span>{{ hint.text }}
      </span>
    </div>
    <!--debug buttons div>
      <v-btn @click="detachScrollbar">detach</v-btn>
      <v-btn @click="updateScrollbar">update</v-btn>
    </div-->
  </div>
</template>

<script>
import FinLexiconText from "@/components/common/lexicon/LexiconText/index"
import {debounce} from "debounce"
import DataFetcher from "@/components/common/DataLoader/DataFetcher"
import perfectscrollbar from "fincraft-lib-basics/src/mixins/perfectscrollbar"
import HeaderValueRender from "@/mixins/HeaderValueRender"

export default {
  name: "FinDataTable",
  components: {FinLexiconText},
  mixins: [perfectscrollbar, HeaderValueRender],
  props: {
    itemClass: {
      type: Function,
    },
    emptyCell: {
      type: String,
      default: "-",
    },
    disableRowHover: {
      type: Boolean,
    },
    fixedHeader: {
      type: Boolean,
    },
    headers: {},
    headerSelect: {},
    height: {
      type: [Number, String],
    },
    hideDefaultHeader: {
      type: Boolean,
    },
    hideDefaultFooter: {
      type: Boolean,
    },
    itemKey: {
      type: String,
      default: "id",
    },
    nativeScroll: {
      type: Boolean,
    },
    /** TODO rename in items */
    resolver: {},
    externalFilter: {},
    showSelect: {
      type: Boolean,
    },
    shrinkToViewPort: {
      type: Boolean,
    },
    sortBy: {
      type: Array,
    },
    sortDesc: {
      type: Array,
    },
    stickyFirstCol: {
      type: Boolean,
    },
    stickyLastCol: {
      type: Boolean,
    },
    transparent: {
      type: Boolean,
    },
    mobileBreakpoint: {
      default: 960,
    },
    noDataText: {
      type: String,
    },
    value: {},
  },
  data() {
    return {
      total: -1,
      items: [],
      loading: true,
      isServerManaged: true,
      options: {},
      panels: {},
      tableWapper: null,
      viewPortHeight: null,
      selected: this.value ? this.value.slice(0) : [],
      noDataTextMsg:
        this.noDataText || this.$vuetify.lang.t("$vuetify.noDataText"),
      perfectScrollbarOpts: {
        minScrollbarLength: 10,
        // If this option is true, when the scroll reaches the end of the side, mousewheel event
        // will be propagated to parent element.
        wheelPropagation: true,
      },
    }
  },
  watch: {
    _adaptedOptions: {
      handler() {
        if (this.isServerManaged) {
          this._fetchData()
        }
        this.$nextTick(() => this.contentChanged(true))
      },
      deep: true,
    },
    _headersWithSelect() {
      this.adaptHeaderForBetterLabeling()
    },
    resolver() {
      this._fetchData()
    },
    externalFilter() {
      this._fetchData()
    },
    _variant: {
      handler() {
        this.recalculateModel()
        this.updateScrollbar()
      },
      deep: true,
    }
  },
  computed: {
    _variant() {
      if (Array.isArray(this.headers)) {
        return this.headers
      } else {
        let result = this.headers
        if (this.headerSelect) {
          result = this.headers[this.headerSelect]
        }
        return result
      }
    },
    _mobileView() {
      return this._variant?.mobileView || null
    },
    _grid() {
      let grid = this._mobileView?.grid
      if (grid && Array.isArray(grid)) {
        grid = {headers: grid}
      }

      return grid
    },
    _gridCols() {
      let cols = this._grid?.conf?.cols || 4
      if (typeof cols === "function") {
        cols = cols(this)
      }
      return cols
    },
    _gridInline() {
      let inline = this._grid?.conf?.inline || false
      if (typeof inline === "function") {
        inline = inline(this)
      }
      return inline
    },
    _list() {
      let list = this._mobileView?.list
      if (list && Array.isArray(list)) {
        list = {headers: list}
      }
      return list
    },
    _listRowProps() {
      let rowProps = this._list?.conf?.rowProps || {}
      if (typeof rowProps === "function") {
        rowProps = rowProps(this)
      }
      return rowProps
    },
    _headers() {
      return this._variant?.headers || this._variant
    },
    _headersWithSelect() {
      if (this.showSelect) {
        return [
          ...this._headers,
          {
            value: "data-table-select",
            align: "center",
            class: "checkbox-select"
          },
        ]
      }
      return this._headers
    },
    _height() {
      let result = undefined
      if (this.height instanceof String && this.height.includes("px")) {
        result = parseInt(this.height.replace("px", "").trim())
      } else if (this.height instanceof String && this.height.includes("vh")) {
        let tmp = parseInt(this.height.replace("vh", "").trim())
        result = (this.viewPortHeight * tmp) / 100
      } else {
        result = this.height
      }

      if (this.shrinkToViewPort && (this.viewPortHeight < result || !result)) {
        result = this.viewPortHeight
      }
      return result
    },
    _adaptedOptions() {
      const adaptedOptions = JSON.parse(JSON.stringify(this.options))
      adaptedOptions.externalFilter = this.externalFilter
      if (adaptedOptions.sortBy?.length > 0) {
        const header = this._headers.find(
          (item) => item.value === adaptedOptions.sortBy[0]
        )
        //TODO allow multisort
        if (header) {
          adaptedOptions.sortByHeader = [header]
          if (header.sortKey) {
            adaptedOptions.sortBy[0] = header.sortKey
          }
        }
      }
      return adaptedOptions
    },

    _hints() {
      return this._headers.reduce((acc, header) => {
        const hint = header.hint
        if (hint) {
          acc[hint.key] = hint
        }
        return acc
      }, {})
    },
    _hintOrder() {
      return Object.values(this._hints).map(h => h.key)
    }
  },
  methods: {
    async _fetchData() {
      this.loading = true
      try {
        const val = await DataFetcher.fetchData(
          this.resolver,
          this._adaptedOptions
        )
        if (val) {
          if (Array.isArray(val)) {
            this.items = val
            this.isServerManaged = false
          } else {
            this.items = val.items
            this.total = val.total
          }
          this.$emit("loaded", val)
        }
      } catch (e) {
        console.error(e)
        this.error = e
        this.$emit("error", e)
      }

      this.recalculateModel()

      this.loading = false
      this.$nextTick(this.contentChanged)
    },
    recalculateModel() {
      this.items = this.adaptModels(this._variant, this.items)
    },
    resize() {
      const app = this.$vuetify.application
      //remove appbars and remove table footer and top
      //TODO improve
      this.viewPortHeight = window.innerHeight - app.footer - app.top - 54 - 56

      this.contentChanged()
    },
    contentChanged(scroll) {
      if (scroll && this.tableWapper) this.tableWapper.scrollTop = 0
      this.updateScrollbar()
    },
    adaptHeaderForBetterLabeling() {
      //TODO this dont work at window resize
      this.$el.querySelectorAll("th > .v-icon").forEach(($icon) => {
        const $label = $icon.parentNode.querySelectorAll("div")[0]
        if ($label) {
          $label.appendChild($icon)
          $label.classList.add("table-header")
        }
      })
    },
    getHintIndex({key}) {
      return this._hintOrder.indexOf(key)
    },
    getGridCols(header) {
      //TODO cols at header configuration
      return this._gridCols
    },

    getScrollElement() {
      return this.tableWapper
    },
  },
  mounted() {
    this.adaptHeaderForBetterLabeling()

    if (!this.nativeScroll) {
      this.tableWapper = this.$refs.table.$el.querySelector(
        ".v-data-table__wrapper"
      )
    }

    this.resize()
  },
}
</script>

<style lang="scss" scoped>

.hints {
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: #99a0a4;
}

.fin-data-table {
  * {
    font-family: $body-font-family;
  }

  &.transparent {
    background-color: transparent;
  }

  &.disable-row-hover ::v-deep tr:hover {
    background-color: transparent !important;
  }

  &.sticky-first-col,
  &.sticky-last-col {
    &:not(.disable-row-hover) ::v-deep {
      .v-data-table__selected td,
      tr:hover td {
        background-color: rgb(245, 245, 245) !important;
      }
    }

    &.sticky-first-col {
      ::v-deep {
        td:first-child,
        th:first-child {
          left: 0;
          border-right: thin solid rgba(0, 0, 0, 0.12);
        }

        table > tbody > tr >,
        table > thead > tr > {
          td:first-child,
          th:first-child {
            background: var(--white);
            position: sticky !important;
            position: -webkit-sticky !important;
            z-index: 5;
          }

          th:first-child {
            z-index: 6;
          }
        }
      }
    }

    &.sticky-last-col {
      ::v-deep {
        td:last-child,
        th:last-child {
          right: -1px;
          border-left: thin solid rgba(0, 0, 0, 0.12);
        }

        table > tbody > tr >,
        table > thead > tr > {
          td:last-child,
          th:last-child {
            background: var(--white);
            position: sticky !important;
            position: -webkit-sticky !important;
            z-index: 5;
            padding-right: 0;

            .checkbox-select {
              padding-right: 8px;
              justify-content: end;
            }
          }

          th:last-child {
            z-index: 6;
          }
        }
      }
    }
  }

  .value-hint {
    margin: 6px 0 0;
    font-family: Gilroy;
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.25;
    letter-spacing: normal;
    color: var(--grey-7);

  }

  ::v-deep {
    .ps__rail-y,
    .ps__rail-x {
      z-index: 7;
      opacity: 0.6;
    }

    th {
      hyphens: manual;
      text-align: center !important;
      vertical-align: top !important;
      letter-spacing: 1px;
      font-weight: 600 !important;
      font-size: 12px;
      color: var(--cool-grey) !important;

      &:hover {
        color: var(--night-blue) !important;
      }

      * {
        text-transform: uppercase !important;
      }

      /* swap order of search and checkbox-select: show select checkbox after search */
      .v-data-table-header-mobile__select {
        order: 1;

        .v-icon.v-icon {
          font-size: 30px;
          color: var(--night-blue);
        }
      }

      .table-header {
        position: relative;
        display: inline-block;

        .v-icon {
          position: absolute;
          right: -20px;
          top: 0;
        }
      }
    }

    td {
      white-space: nowrap;
      font-size: 16px !important;
      font-weight: normal !important;
      color: var(--night-blue) !important;

      .mobile-header {
        color: var(--night-blue) !important;
        letter-spacing: 1px;
        font-size: 12.2px !important;
        font-weight: 700 !important;
        white-space: normal;

        * {
          text-transform: uppercase !important;
        }
      }

      @media only screen and (max-width: 600px) {

        .mobile-header {
          letter-spacing: 0.3px;
          font-weight: 450 !important;
        }
      }
    }
  }

  .expansion ::v-deep {
    .v-expansion-panel {
      background-color: transparent;
    }

    .v-expansion-panel-header {
      padding: 0;
    }
  }

  .mobile-data-table {
    display: block;

    tr {
      display: flex;
      flex-direction: column;
      height: unset !important;

      overflow-x: hidden;
    }

    ::v-deep .detailList {
      .paragraph {
        font-size: 16px;
        color: var(--night-blue);
        font-weight: normal;
      }

      .value {
        span {
          font-size: 16px;
        }

      }
    }

    @media only screen and (max-width: 600px) {
      ::v-deep .value {
        font-size: 12px !important;
        font-weight: bold;
      }

      ::v-deep .fin-time-ago {
      }

      ::v-deep .v-progress-circular {
        font-size: 18px !important;
      }
    }


    td {
      white-space: normal;
    }
  }

  @media only screen and (max-width: 600px) {
    thead.v-data-table-header-mobile tr th,
    .mobile-data-table tr td {
      border: none !important;
    }

    ::v-deep .v-data-footer {
      margin: 0;

      .v-data-footer__select {
        .v-input {
          margin-left: 10px;
        }
      }

      .v-data-footer__pagination {
        margin: 0;
      }
    }
  }
}
</style>
